import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { IA_MONITORING_DEFAULT_PERIOD } from '../constant/iaMonitoringConstant';
import type { IaMonitoringScreenFilter, IaMonitoringState } from '../objects/iaMonitoringState';
import type {
    IaIndicatorDetail,
    IaMonitoringDomain,
    IaMonitoringFilterOptions,
    IaMonitoringOverview
} from '../objects/iaMonitoringTypes';

const initialState: IaMonitoringState = {
    ia_monitoring_filter: { days: IA_MONITORING_DEFAULT_PERIOD },
    ia_monitoring_expert: false,
    ia_monitoring_overview: null,
    ia_monitoring_overview_loading: false,
    ia_monitoring_options: null,
    ia_monitoring_detail: null,
    ia_monitoring_detail_domain: null,
    ia_monitoring_detail_loading: false
};

// ── Reducer functions ───────────────────────────────────────────────────────

/** Un filtre changé remplace le précédent champ par champ ; une plateforme changée vide le client. */
const updateIaMonitoringFilterReducer = (
    state: IaMonitoringState,
    action: PayloadAction<Partial<IaMonitoringScreenFilter>>
): IaMonitoringState => ({ ...state, ia_monitoring_filter: { ...state.ia_monitoring_filter, ...action.payload } });

const toggleIaMonitoringExpertReducer = (state: IaMonitoringState): IaMonitoringState =>
    ({ ...state, ia_monitoring_expert: !state.ia_monitoring_expert });

const updateIaMonitoringOverviewReducer = (
    state: IaMonitoringState,
    action: PayloadAction<{ overview: IaMonitoringOverview; options: IaMonitoringFilterOptions }>
): IaMonitoringState => ({
    ...state,
    ia_monitoring_overview: action.payload.overview,
    ia_monitoring_options: action.payload.options
});

const updateIaMonitoringOverviewLoadingReducer = (
    state: IaMonitoringState,
    action: PayloadAction<boolean>
): IaMonitoringState => ({ ...state, ia_monitoring_overview_loading: action.payload });

const updateIaMonitoringDetailReducer = (
    state: IaMonitoringState,
    action: PayloadAction<{ domain: IaMonitoringDomain | null; detail: IaIndicatorDetail | null }>
): IaMonitoringState => ({
    ...state,
    ia_monitoring_detail: action.payload.detail,
    ia_monitoring_detail_domain: action.payload.domain
});

const updateIaMonitoringDetailLoadingReducer = (
    state: IaMonitoringState,
    action: PayloadAction<boolean>
): IaMonitoringState => ({ ...state, ia_monitoring_detail_loading: action.payload });

// ── Slice ─────────────────────────────────────────────────────────────────────

export const iaMonitoring = createSlice({
    name: 'iaMonitoring',
    initialState,
    reducers: {
        updateIaMonitoringFilter: updateIaMonitoringFilterReducer,
        toggleIaMonitoringExpert: toggleIaMonitoringExpertReducer,
        updateIaMonitoringOverview: updateIaMonitoringOverviewReducer,
        updateIaMonitoringOverviewLoading: updateIaMonitoringOverviewLoadingReducer,
        updateIaMonitoringDetail: updateIaMonitoringDetailReducer,
        updateIaMonitoringDetailLoading: updateIaMonitoringDetailLoadingReducer
    }
});

export const {
    updateIaMonitoringFilter,
    toggleIaMonitoringExpert,
    updateIaMonitoringOverview,
    updateIaMonitoringOverviewLoading,
    updateIaMonitoringDetail,
    updateIaMonitoringDetailLoading
} = iaMonitoring.actions;

export default iaMonitoring.reducer;
